/* eslint-disable import/prefer-default-export */
import CashFlowTablesEnum from '@/views/cashFlowGroupers/mainGrouper/enums/CashFlowTablesEnum';
import ITotalsData from '../../interfaces/groupers/ITotalsData';
import IPredictedFlowGrouperList from '../../interfaces/groupers/IPredictedFlowGrouperList';
import { ICalculateTotalsByGroupParams, ICalculateTotalsParams } from '../../interfaces/ICashFlowList';

function getTotalsFromSpecificGroup(
  grouperName: CashFlowTablesEnum,
  destructuredBankBalance: IPredictedFlowGrouperList[],
): number[] {
  const totalsAdded: number[] = [];

  for (let i = 0; i < destructuredBankBalance.length; i += 1) {
  const { level, totals, grouperType } = destructuredBankBalance[i];
  const levelToBeConsidered = grouperType === CashFlowTablesEnum.BANKS ? 1 : 0;

  if (grouperType === grouperName && level === levelToBeConsidered) {
      for (let totalsIndex = 0; totalsIndex < totals.length; totalsIndex += 1) {
      const { value } = totals[totalsIndex];

      totalsAdded[totalsIndex] = (totalsAdded[totalsIndex] ?? 0) + value;
      }
    }
  }

  return totalsAdded;
}

function getAllAccountsFromSameGroupAndLevel(
  level: number,
  grouperType: CashFlowTablesEnum,
  destructuredBankBalance: IPredictedFlowGrouperList[],
) {
  const filteredByLevel: IPredictedFlowGrouperList[] = [];

  for (let i = 0; i < destructuredBankBalance.length; i += 1) {
    const {
      level: registerLevel,
      grouperType: registerGrouperType,
      isSimulation,
    } = destructuredBankBalance[i];

    if (
      registerLevel === level
      && registerGrouperType === grouperType
      && !isSimulation
    ) {
      filteredByLevel.push(destructuredBankBalance[i]);
    }
  }

  return filteredByLevel;
}

function mustShowTotalValue(
  cashFlowSum: boolean,
  totalData: ITotalsData,
): ITotalsData {
  const totalDataWithoutValue = { value: 0, disregardValue: totalData.disregardValue };

  if (cashFlowSum && totalData.disregardValue) {
    return totalDataWithoutValue;
  }

  if (cashFlowSum || !Object.prototype.hasOwnProperty.call(totalData, 'isReleased')) {
    return totalData;
  }

  if (totalData.isReleased) {
    return totalData;
  }

  return totalDataWithoutValue;
}

function grouperRegisterByLevelAndParentIndex(
  level: number,
  grouperType: CashFlowTablesEnum,
  destructuredBankBalance: IPredictedFlowGrouperList[],
  defaultTotalsArray: ITotalsData[],
  cashFlowSum: boolean,
) {
  const filteredByLevel: IPredictedFlowGrouperList[] = getAllAccountsFromSameGroupAndLevel(
    level,
    grouperType,
    destructuredBankBalance,
  );

  for (let baseIndex = 0; baseIndex < filteredByLevel.length; baseIndex += 1) {
    const { totals, identifier } = filteredByLevel[baseIndex];

    const childTotals = destructuredBankBalance
      .filter(({ parentIdentifier }) => parentIdentifier === identifier)
      .map(
        (data) => data.totals
          .map((totalData): ITotalsData => mustShowTotalValue(cashFlowSum, totalData)),
      );

    const calculatedTotals = childTotals.reduce(
      (acc, row) => row.map(
        (value, index) => ({
          value: acc[index].value + value.value,
          disregardValue: acc[index].disregardValue,
        }),
      ),
      defaultTotalsArray,
    );

    for (let totalsIndex = 0; totalsIndex < totals.length; totalsIndex += 1) {
      totals[totalsIndex].value = calculatedTotals[totalsIndex].value;
    }
  }
}

export const calculateTotalsByGroup = (params: ICalculateTotalsByGroupParams): void => {
  const {
    grouperType,
    cashFlowListData,
    destructuredBankBalance,
    mustSumPredictedFlowValues,
  } = params;

  const defaultTotalsArray: ITotalsData[] = new Array(cashFlowListData!.totals.length)
    .fill({ value: 0 });
  const levelsOrderToCalculate = [2, 1, 0];

  if (grouperType !== CashFlowTablesEnum.BANKS) {
    levelsOrderToCalculate.unshift(3);
  }

  for (let i = 0; i < levelsOrderToCalculate.length; i += 1) {
    const orderLevel = levelsOrderToCalculate[i];

    grouperRegisterByLevelAndParentIndex(
      orderLevel,
      grouperType,
      destructuredBankBalance,
      defaultTotalsArray,
      mustSumPredictedFlowValues,
    );
  }
};

export const calculateTotalValue = (params: ICalculateTotalsParams): void => {
  const {
    cashFlowListData,
    destructuredBankBalance,
    shouldConsiderInitialBalanceOnPredictedFlow,
    totalsRow,
  } = params;

  const accountsReceivableTotals = getTotalsFromSpecificGroup(
    CashFlowTablesEnum.ACCOUNTS_RECEIVABLE,
    destructuredBankBalance,
  );
  const accountPayableTotals = getTotalsFromSpecificGroup(
    CashFlowTablesEnum.ACCOUNTS_PAYABLE,
    destructuredBankBalance,
  );
  const bankLevelOneTotals = getTotalsFromSpecificGroup(
    CashFlowTablesEnum.BANKS,
    destructuredBankBalance,
  );

  const bankLevelZero = destructuredBankBalance.find(
    (({ grouperType, level }) => grouperType === CashFlowTablesEnum.BANKS && level === 0),
  );

  if (!bankLevelZero) {
    return;
  }

  bankLevelZero.totals[0].value = shouldConsiderInitialBalanceOnPredictedFlow
    ? cashFlowListData!.previousValueBankBalance : 0;

  for (let i = 0; i < totalsRow.length; i += 1) {
    const actualTotalRow = totalsRow[i];

    bankLevelZero.totals[i].value += bankLevelOneTotals[i];

    const bankAddedValue = bankLevelZero.totals[i].value ?? 0;
    const accountsReceivableAddedValue = accountsReceivableTotals[i] ?? 0;
    const accountPayableAddedValue = accountPayableTotals[i] ?? 0;

    actualTotalRow.value = (
      (bankAddedValue + accountsReceivableAddedValue)
      - accountPayableAddedValue
    );

    if (bankLevelZero.totals[i + 1] !== undefined) {
      bankLevelZero.totals[i + 1].value = shouldConsiderInitialBalanceOnPredictedFlow
        ? actualTotalRow.value : 0;
    }
  }
};
